import React, { ReactNode, RefObject, createContext, useContext, useId, useMemo } from 'react';

import { getDocumentBody } from '../dom/getDocumentBody/getDocumentBody';

export const DEFAULT_ROOT_LAYER_ID = 'defaultRootLayerId';

export const layerManagerIgnoreOnDismissClassName = 'layerManagerIgnoreOnDismiss';

/**
 * Базовые слои
 */
export const LayerPriorities = {
    /**
     * Стандартный приоритет
     */
    Default: 1,
    /**
     * Повышенный приоритет, используется если нужно показать что-то выше стандартных модалок
     */
    Increased: 2,
    /**
     * Приоритет, блокирующими работу со страницей
     */
    PageBlocking: 3,
} as const;
export type LayerPriority = (typeof LayerPriorities)[keyof typeof LayerPriorities];

/**
 * Фиксированные слои, используются в редких кейсах, по типу Confirm, Captcha, Toast
 *
 * @remarks У вас не должно быть необходимости импорта данных значений, но если нужно, то обсудите с командой библиотеки
 */
export const LayerFixedPriorities = {
    /**
     * Приоритет для отображения шторки
     */
    SidePanel: 0,
    /**
     * Приоритет для модалок подтверждения
     */
    Confirm: 4,
    /**
     * Приоритет, технически блокирующий работу со страницей
     *
     * В этом приоритете может быть антифрод капча
     */
    TechnicalBlocking: 5,
    /**
     * Приоритет, предназначенный для отображения тостов
     */
    Toast: 6,
} as const;
export type LayerFixedPriority = (typeof LayerFixedPriorities)[keyof typeof LayerFixedPriorities];

export type LayerPriorityType = LayerPriority | LayerFixedPriority;

/**
 * В Select dna указан zIndex 41000, после полного выпила всех мест его использования нужно будет опустить до 10000
 */
const MINIMAL_Z_INDEX = 50000;
const Z_INDEX_LAYER_STEP = 1000;

interface LayerManagerConstructorProps {
    readonly priority: LayerPriorityType;
    readonly parent?: LayerManager;
    readonly contextPopupsContainerRef?: RefObject<HTMLElement>;
    readonly id: string;
}

function getMaxPriority(...priorities: LayerPriorityType[]): LayerPriorityType {
    // eslint-disable-next-line @typescript-eslint/consistent-type-assertions --- работаем только с Priority
    return Math.max(...priorities) as LayerPriorityType;
}

export class LayerManager {
    /**
     * Приоритет слоя, определяет как будут высчитываться его zIndex
     *
     * @default Priorities.Default
     */
    readonly priority: LayerPriorityType;
    /**
     * Родительский слой
     */
    readonly parent?: LayerManager;
    /**
     * Контейнер для отрисовки контекстных всплывашек
     */
    private readonly _contextPopupsContainerRef: RefObject<HTMLElement> | undefined;

    readonly id: string;

    constructor(
        {
            priority = LayerPriorities.Default,
            parent,
            contextPopupsContainerRef,
            id = DEFAULT_ROOT_LAYER_ID,
        }: LayerManagerConstructorProps = {
            priority: LayerPriorities.Default,
            id: DEFAULT_ROOT_LAYER_ID,
        },
    ) {
        this.parent = parent;
        // В случае, если новый слой открывается внутри родителя с большим приоритетом, то заменяем на родительский
        this.priority = parent ? getMaxPriority(priority, parent.priority) : priority;
        this._contextPopupsContainerRef = contextPopupsContainerRef ?? parent?._contextPopupsContainerRef;
        this.id = id;
    }

    // Т.к. контекстные элементы отрисовываются внутри модалки, zIndex может быть любым, главное меньше MINIMAL_Z_INDEX
    // Его всё равно перекроет новый слой
    readonly zIndexForContextPopups = 6000;

    get zIndex(): number {
        const { parent } = this;
        // В случае, если это рутовый слой, или текущий слой выше родительского,
        // То высчитываем начальный zIndex этого приоритета
        if (!parent || this.priority > parent.priority) {
            return MINIMAL_Z_INDEX + Z_INDEX_LAYER_STEP * this.priority;
        }

        // В ином случае выставляем zIndex на 1 выше родительского
        return parent.zIndex + 1;
    }

    // Вызывается внутри Modal / Drawer
    getNewLayer({
        priority = LayerPriorities.Default,
        contextPopupsContainerRef,
        id,
    }: {
        priority?: LayerPriorityType;
        contextPopupsContainerRef?: RefObject<HTMLElement>;
        id: string;
    }): LayerManager {
        return new LayerManager({
            priority,
            parent: this,
            contextPopupsContainerRef,
            id,
        });
    }

    // Будет использоваться в местах, где не нужно поднимать zIndex,
    // но будет изменен контейнер для отрисовки контекстных попапов (напр. навигация в редактировании кампаний)
    getContextPopupsContainerLayer(contextPopupsContainerRef: RefObject<HTMLElement>): LayerManager {
        return new LayerManager({
            priority: this.priority,
            parent: this.parent,
            contextPopupsContainerRef,
            id: this.id,
        });
    }

    get contextPopupsContainer(): HTMLElement | null {
        return this._contextPopupsContainerRef?.current ?? getDocumentBody() ?? null;
    }
}

const LayerContext = createContext<LayerManager>(new LayerManager());
export const LayerContextProvider = LayerContext.Provider;
// Используется в тестах, наружу не экспортим
export const useLayerContext = () => useContext(LayerContext);

interface UseNewLayerProps {
    /**
     * Приоритет для отрисовки нового слоя
     */
    priority?: LayerPriorityType;
    /**
     * Контейнер для отрисовки контекстных попапов
     */
    contextPopupsContainerRef?: RefObject<HTMLElement>;
}

/**
 * Создаёт новый слой с уровнем вложенности на 1 больше родительского
 * К тому-же позволяет выставить новый контейнер для рендера контекстных всплывашек
 *
 * @returns Новый слой с уровнем вложенности на 1 больше родительского
 */
export const useNewLayer = ({ priority, contextPopupsContainerRef }: UseNewLayerProps) => {
    const parentLayerManager = useLayerContext();
    const id = useId();

    return useMemo(() => {
        return parentLayerManager.getNewLayer({
            priority, // может быть undefined, тогда приравняется к Default слою
            contextPopupsContainerRef,
            id,
        });
    }, [parentLayerManager, priority, contextPopupsContainerRef, id]);
};

/**
 * Создаёт слой такой же по уровню вложенности как родительский,
 * но с измененным контейнером для рендера контекстных всплывашек
 * И оборачивает в контекст дальнейшее React дерево
 */
export const ProvideContextPopupsContainer = ({
    children,
    contextPopupsContainerRef,
}: {
    children: ReactNode;
    contextPopupsContainerRef: RefObject<HTMLElement>;
}) => {
    const parentLayerManager = useLayerContext();

    const siblingLayer = useMemo(() => {
        return parentLayerManager.getContextPopupsContainerLayer(contextPopupsContainerRef);
    }, [parentLayerManager, contextPopupsContainerRef]);

    return <LayerContextProvider value={siblingLayer}>{children}</LayerContextProvider>;
};
